import type { Money } from '@/api/types';

const money = new Map<string, Intl.NumberFormat>();

export function formatMoney(m: Money): string {
  let f = money.get(m.currency);
  if (!f) {
    f = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: m.currency, maximumFractionDigits: 0 });
    money.set(m.currency, f);
  }
  return f.format(m.amountMinor / 100);
}

const dayMonth = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
const full = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });

/** Date-only strings ("2026-10-01") are calendar days, not UTC midnights. */
export function toDate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
}

export const formatDate = (iso: string) => dayMonth.format(toDate(iso));
export const formatDateFull = (iso: string) => full.format(toDate(iso));

/** «сегодня, 15:30», «вчера», «27 сентября» — for message and activity stamps. */
export function formatWhen(iso: string, now = new Date()): string {
  const d = toDate(iso);
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (days === 0) return `сегодня, ${time.format(d)}`;
  if (days === 1) return `вчера, ${time.format(d)}`;
  return dayMonth.format(d);
}

/** «через 2 дня», «сегодня», «3 дня назад» — for due dates. */
export function formatDue(iso: string, now = new Date()): string {
  const days = Math.round((startOfDay(toDate(iso)) - startOfDay(now)) / 86_400_000);
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  if (days > 1) return `через ${days} ${plural(days, 'день', 'дня', 'дней')}`;
  const ago = -days;
  return `${ago} ${plural(ago, 'день', 'дня', 'дней')} назад`;
}

/** «только что», «5 мин назад», «3 ч назад», then the calendar form — for «last worked» stamps. */
export function formatAgo(iso: string, now = new Date()): string {
  const min = Math.floor((now.getTime() - toDate(iso).getTime()) / 60_000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} ч назад`;
  return formatWhen(iso, now);
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export const formatSize = (kb: number) => (kb >= 1024 ? `${(kb / 1024).toFixed(1).replace('.', ',')} МБ` : `${kb} КБ`);

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export const formatTime = (iso: string) => time.format(new Date(iso));

/** Local calendar day of a timestamp, for grouping. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

/** «Сегодня», «Вчера», «понедельник, 22 сентября», «12 марта 2025». */
export function formatDayHeading(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (days === 0) return 'Сегодня';
  if (days === 1) return 'Вчера';
  if (days < 7) return weekday.format(d);
  return d.getFullYear() === now.getFullYear() ? dayMonth.format(d) : full.format(d);
}

/** What is still owed on an invoice: total minus paid. The BFF charges the same amount. */
export const balanceOf = (inv: { total: Money; paid: Money }): Money => ({ ...inv.total, amountMinor: inv.total.amountMinor - inv.paid.amountMinor });

/** A line's quantity as people write it: «2,5», «1». */
export const formatQuantity = (q: number) => String(q).replace('.', ',');
